import React, { useState, useCallback } from 'react';

/* ─────────────────────────────────────────────
   STYLES
───────────────────────────────────────────── */
const styles = `
  /* Variables and font imports are handled by global.css */

  .services-section {
    padding: 100px 60px;
    position: relative;
    overflow: hidden;
    background: var(--black);
  }
  .services-section::before {
    content: '';
    position: absolute; inset: 0; pointer-events: none;
    background: radial-gradient(ellipse 50% 40% at 80% 20%, rgba(245,200,0,0.05) 0%, transparent 60%);
  }

  /* ── Header ── */
  .svc-header {
    display: flex; justify-content: space-between;
    align-items: flex-end; margin-bottom: 64px;
    gap: 32px; flex-wrap: wrap;
  }
  .svc-eyebrow {
    font-size: 10px; letter-spacing: 4px; text-transform: uppercase;
    color: var(--accent); margin-bottom: 14px;
    display: flex; align-items: center; gap: 10px;
  }
  .svc-eyebrow::before {
    content: ''; width: 24px; height: 1px;
    background: var(--accent); display: inline-block;
  }
  .svc-heading {
    font-family: 'Bebas Neue', sans-serif;
    font-size: clamp(42px, 5.5vw, 74px);
    line-height: .95; color: var(--white);
  }
  .svc-heading em {
    font-family: 'Instrument Serif', serif;
    font-style: italic; color: var(--gold);
  }
  .svc-subtitle {
    max-width: 300px; font-size: 13px;
    line-height: 1.85; color: var(--muted-light);
  }

  /* ── Filters ── */
  .filter-row {
    display: flex; gap: 8px; flex-wrap: wrap;
    margin-bottom: 48px;
  }
  .filter-btn {
    font-size: 10px; letter-spacing: 1.5px; text-transform: uppercase;
    padding: 7px 16px; border-radius: 100px; cursor: pointer;
    border: 1px solid var(--border-soft); color: var(--muted-light);
    background: none; font-family: 'DM Sans', sans-serif;
    transition: border-color .2s, color .2s, background .2s;
  }
  .filter-btn:hover {
    border-color: rgba(245,200,0,0.35);
    color: var(--accent);
    background: rgba(245,200,0,0.04);
  }
  .filter-btn.active {
    border-color: rgba(245,200,0,0.35);
    color: var(--accent);
    background: rgba(245,200,0,0.05);
  }

  /* ── Grid ── */
  .services-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    border: 1px solid var(--border-soft);
    border-radius: 6px; overflow: hidden;
  }

  /* ── Card ── */
  .svc-card {
    padding: 40px 28px;
    border-bottom: 1px solid var(--border-soft);
    background: var(--card-bg);
    position: relative; overflow: hidden; cursor: default;
    transition: background .35s ease;
  }
  .svc-card.border-right { border-right: 1px solid var(--border-soft); }
  .svc-card::after {
    content: ''; position: absolute;
    bottom: 0; left: 0; right: 0; height: 2px;
    background: var(--accent);
    transform: scaleX(0); transform-origin: left;
    transition: transform .35s ease;
  }
  .svc-card:hover { background: #191700; }
  .svc-card:hover::after { transform: scaleX(1); }
  .svc-card:hover .svc-icon-wrap { transform: scale(1.12) rotate(-6deg); color: var(--gold); }
  .svc-card:hover .svc-card-num { opacity: 1; }
  .svc-card:hover .svc-bg-num { opacity: 1; transform: scale(1.1); }
  .svc-card:hover .svc-tech-tag { opacity: 1; transform: translateY(0); }

  .svc-bg-num {
    position: absolute; bottom: -10px; right: 12px;
    font-family: 'Bebas Neue', sans-serif; font-size: 96px;
    color: rgba(245,200,0,0.04); line-height: 1;
    pointer-events: none; user-select: none;
    opacity: .6; transition: opacity .4s, transform .4s;
  }

  .svc-card-num {
    font-family: 'Bebas Neue', sans-serif; font-size: 11px;
    color: var(--accent); letter-spacing: 2px;
    margin-bottom: 28px; opacity: .5;
    transition: opacity .3s;
    display: flex; align-items: center; gap: 8px;
  }
  .svc-card-num::after {
    content: ''; flex: 1; height: 1px;
    background: rgba(245,200,0,0.12);
  }

  .svc-icon-wrap {
    color: var(--accent); margin-bottom: 22px;
    transition: transform .35s ease, color .3s;
    display: block; width: 38px; height: 38px;
  }
  .svc-icon-wrap svg { width: 100%; height: 100%; }

  .svc-card-name {
    font-family: 'Bebas Neue', sans-serif; font-size: 19px;
    letter-spacing: 1.5px; color: var(--white);
    margin-bottom: 10px; line-height: 1.1;
  }
  .svc-card-desc {
    font-size: 12px; line-height: 1.8;
    color: var(--muted-light); margin-bottom: 20px;
  }

  .svc-tech-tag {
    display: inline-block; font-size: 9px;
    letter-spacing: 1.5px; text-transform: uppercase;
    padding: 3px 9px; border-radius: 100px;
    border: 1px solid var(--border); color: var(--muted);
    opacity: 0; transform: translateY(6px);
    transition: opacity .3s .1s, transform .3s .1s;
  }

  /* ── Bottom row ── */
  .svc-bottom-row {
    display: flex; align-items: center; justify-content: space-between;
    margin-top: 40px; padding-top: 32px;
    border-top: 1px solid var(--border-soft);
    flex-wrap: wrap; gap: 20px;
  }
  .svc-count-label { font-size: 12px; color: var(--muted-light); }
  .svc-count-label strong { color: var(--white); font-weight: 500; }

  .cta-group { display: flex; gap: 12px; align-items: center; }

  .btn-primary {
    display: inline-flex; align-items: center; gap: 8px;
    background: var(--accent); color: var(--black);
    padding: 11px 28px; font-size: 11px; letter-spacing: 2px;
    text-transform: uppercase; text-decoration: none;
    border-radius: 2px; font-family: 'DM Sans', sans-serif;
    font-weight: 500; border: none; cursor: pointer;
    transition: background .25s, transform .2s;
  }
  .btn-primary:hover { background: var(--gold); transform: translateY(-1px); }

  .btn-ghost {
    display: inline-flex; align-items: center; gap: 8px;
    color: var(--muted-light); font-size: 11px; letter-spacing: 2px;
    text-transform: uppercase; text-decoration: none;
    background: none; border: none; cursor: pointer;
    font-family: 'DM Sans', sans-serif;
    transition: color .25s, gap .25s;
  }
  .btn-ghost:hover { color: var(--white); gap: 14px; }

  /* ── Responsive ── */
  @media (max-width: 1100px) {
    .services-grid { grid-template-columns: repeat(2, 1fr); }
  }
  @media (max-width: 640px) {
    .services-section { padding: 72px 20px; }
    .services-grid { grid-template-columns: 1fr; }
    .svc-header { flex-direction: column; align-items: flex-start; margin-bottom: 44px; }
  }
`;

/* ─────────────────────────────────────────────
   DATA
───────────────────────────────────────────── */
const SERVICES = [
  {
    num: '01', cat: 'development', tag: 'React · Node · Next.js',
    name: 'Web Development',
    desc: 'Fast, scalable, pixel-perfect websites built for performance and conversion.',
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" /></svg>,
  },
  {
    num: '02', cat: 'development', tag: 'iOS · Android · Flutter',
    name: 'App Development',
    desc: 'Native and cross-platform mobile apps that users love to use every day.',
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2" /><path d="M12 18h.01" /></svg>,
  },
  {
    num: '03', cat: 'marketing', tag: 'Google · Meta · LinkedIn',
    name: 'Digital Marketing',
    desc: 'Data-driven campaigns that turn clicks into customers across every channel.',
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2" /></svg>,
  },
  {
    num: '04', cat: 'marketing', tag: 'On-page · Technical · Links',
    name: 'SEO Optimization',
    desc: 'Dominate search rankings and drive organic traffic that compounds over time.',
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>,
  },
  {
    num: '05', cat: 'design', tag: 'Figma · Research · Prototype',
    name: 'UI/UX Design',
    desc: 'Intuitive interfaces and seamless experiences that delight and retain users.',
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M12 2v2M12 20v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M2 12h2M20 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" /></svg>,
  },
  {
    num: '06', cat: 'design', tag: 'Logo · Identity · Guidelines',
    name: 'Branding',
    desc: 'Strategic brand identities that stand out, resonate, and build lasting loyalty.',
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" /></svg>,
  },
  {
    num: '07', cat: 'content', tag: 'Reels · Ads · Brand Films',
    name: 'Video Editing',
    desc: 'Cinematic content that tells your story and stops the scroll instantly.',
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" /></svg>,
  },
  {
    num: '08', cat: 'content', tag: 'Strategy · Content · Analytics',
    name: 'Social Media Management',
    desc: 'Grow your audience with strategic, consistent content across every platform.',
    icon: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 2H7a5 5 0 0 0-5 5v10a5 5 0 0 0 5 5h10a5 5 0 0 0 5-5V7a5 5 0 0 0-5-5z" /><circle cx="12" cy="12" r="3" /><circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" stroke="none" /></svg>,
  },
];

const FILTERS = [
  { label: 'All Services', value: 'all' },
  { label: 'Development', value: 'development' },
  { label: 'Marketing', value: 'marketing' },
  { label: 'Design & Brand', value: 'design' },
  { label: 'Content', value: 'content' },
];

/* ─────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────── */
const LandingServices = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  const visible = activeFilter === 'all'
    ? SERVICES
    : SERVICES.filter(s => s.cat === activeFilter);

  // Decide which cards get a right border based on current column count
  // 4-col on desktop, 2-col on tablet, 1-col on mobile
  // We handle this via CSS classes; for JS we compute cols at 4
  const getCardClasses = useCallback((idx) => {
    const cols = 4; // CSS overrides for smaller screens
    const hasRight = (idx + 1) % cols !== 0 && idx < visible.length - 1;
    return `svc-card${hasRight ? ' border-right' : ''}`;
  }, [visible.length]);

  return (
    <>
      <style>{styles}</style>

      <section className="services-section" id="services">

        {/* Header */}
        <div className="svc-header">
          <div>
            <p className="svc-eyebrow">What We Do</p>
            <h2 className="svc-heading">
              Our <em>Services</em>
            </h2>
          </div>
          <p className="svc-subtitle">
            Every service engineered to push your brand forward in a
            competitive digital landscape.
          </p>
        </div>

        {/* Filter tabs */}
        <div className="filter-row">
          {FILTERS.map(f => (
            <button
              key={f.value}
              className={`filter-btn${activeFilter === f.value ? ' active' : ''}`}
              onClick={() => setActiveFilter(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="services-grid">
          {visible.map((s, i) => (
            <div key={s.num} className={getCardClasses(i)}>
              {/* Big background number */}
              <div className="svc-bg-num">{s.num}</div>

              {/* Number row */}
              <p className="svc-card-num">{s.num}</p>

              {/* Icon */}
              <span className="svc-icon-wrap">{s.icon}</span>

              {/* Name */}
              <p className="svc-card-name">{s.name}</p>

              {/* Description */}
              <p className="svc-card-desc">{s.desc}</p>

              {/* Tech tag (appears on hover) */}
              <span className="svc-tech-tag">{s.tag}</span>
            </div>
          ))}
        </div>

        {/* Bottom row */}
        <div className="svc-bottom-row">
          <p className="svc-count-label">
            Showing <strong>{visible.length}</strong> of {SERVICES.length} services
          </p>
          <div className="cta-group">
            <a href="#contact" className="btn-primary">
              Get a Free Quote
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </a>
            <a href="#about" className="btn-ghost">
              Learn More →
            </a>
          </div>
        </div>

      </section>
    </>
  );
};

export default LandingServices;